import {
  Button,
  Card,
  Close,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ELinkVariant,
  EModalSize,
  EModalVariants,
  ETextStyleVariant,
  EToastVariant,
  GeneralStarFillIcon,
  Heading,
  Link,
  Loader,
  Modal,
  Text,
} from '@outdoorsyco/bonfire';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { Swiper as TSwiper } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import AspectRatio from '@/components/common/AspectRatio';
import { PET_NOT_ALLOWED } from '@/components/switchback/Icon/assets';
import Icon from '@/components/switchback/Icon/IconComponent';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRouter } from '@/hooks/useRouter';
import { useToast } from '@/hooks/useToast';
import { updateQuoteDeliveryFromURL } from '@/redux/modules/delivery/quoteDelivery';
import { getListing } from '@/redux/modules/listing';
import { getQuote } from '@/redux/modules/quote';
import { TRootState } from '@/redux/rootReducer';
import {
  getListingGeneratorRules,
  getListingMileageRules,
} from '@/redux/selectors/listing/houseRules';
import { getListingHeader } from '@/redux/selectors/listing/listingHeader';
import { getFromAndTo } from '@/redux/selectors/queryParams';
import { IRentalTile } from '@/utility/mapSearchResultToTile';
import { getParams } from '@/utility/queryParams';
import { useThunkDispatch } from '@/utility/redux/dispatch';

import { PriceDetails } from '../PriceDetails';
import css from './QuickPreviewModal.module.css';

const infosOrder = [
  'guests',
  'sleeps',
  'length',
  'setup-only',
  'minimum-age',
  'pet-friendly',
  'offers-delivery',
];

type TQuickPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  rentalTile: IRentalTile;
  onClickViewListing?: () => void;
};

export const QuickPreviewModal = ({
  isOpen,
  onClose,
  rentalTile,
  onClickViewListing,
}: TQuickPreviewModalProps) => {
  const { id, details, title, photoUrl, photoAlt, price, linkUrl, slug } = rentalTile;
  const { classText, location, ratingNumber, ratingQtd } = details || {};

  const intl = useIntl();
  const router = useRouter();
  const { isMobile, isAboveDesktop } = useBreakpoint();
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const dispatch = useThunkDispatch();
  const { showToast } = useToast();
  const isLoadingListing = useSelector((state: TRootState) => state.listing.isLoading);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const listingHeader = useSelector(getListingHeader);
  const mileageRules = useSelector((state: TRootState) => getListingMileageRules(state, false));
  const generatorRules = useSelector(getListingGeneratorRules);
  const queryFromAndTo = useSelector(getFromAndTo, isEqual);

  const [mainSwiperInstance, setMainSwiperInstance] = useState<TSwiper>();
  const [thumbsSwiperInstance, setThumbsSwiperInstance] = useState<TSwiper>();

  const hasDates = queryFromAndTo.from && queryFromAndTo.to;

  const priceDetails = price && (
    <PriceDetails showDiscount nights={price.estimatedPriceBreakdown?.duration} {...price} />
  );

  const cta = useMemo(() => {
    if (!hasDates) {
      return intl.formatMessage({
        defaultMessage: 'Check availability',
        id: '6h+4pN',
      });
    }

    if (price?.instantBook) {
      return intl.formatMessage({ defaultMessage: 'Instant book', id: 'jC5Ueq' });
    }

    return intl.formatMessage({ defaultMessage: 'Review request', id: 'MKR5Bp' });
  }, [hasDates, price, intl]);

  const [infos, isPetFriendly] = useMemo(() => {
    if (!listingHeader || !Array.isArray(listingHeader.infos)) {
      return [[], false, false];
    }

    listingHeader.infos.sort((a, b) => infosOrder.indexOf(a.key) - infosOrder.indexOf(b.key));

    const isPetFriendly = listingHeader.infos.find(info => info.key === 'pet-friendly');
    const offersDelivery = listingHeader.infos.find(info => info.key === 'offers-delivery');

    return [listingHeader.infos, isPetFriendly, offersDelivery];
  }, [listingHeader]);

  useEffect(() => {
    dispatch(getListing((rentalTile.id || 0).toString()));
  }, [dispatch, rentalTile.id]);

  useEffect(() => {
    if (!mainSwiperInstance) return;

    const handleRealIndexChange = (swiper: TSwiper) => {
      setSelectedPhotoIndex(swiper.realIndex);
    };

    mainSwiperInstance.on('realIndexChange', handleRealIndexChange);

    return () => {
      mainSwiperInstance.off('realIndexChange', handleRealIndexChange);
    };
  }, [mainSwiperInstance]);

  const handleThumbnailClick = useCallback(
    (index: number) => {
      mainSwiperInstance?.slideTo(index);
    },
    [mainSwiperInstance],
  );

  useEffect(() => {
    if (!mainSwiperInstance) return;

    const changeActiveSlide = (e: KeyboardEvent) => {
      if (!mainSwiperInstance) return;

      if (e.code === 'ArrowLeft') {
        mainSwiperInstance.slidePrev();
      }

      if (e.code === 'ArrowRight') {
        mainSwiperInstance.slideNext();
      }
    };

    document.addEventListener('keydown', changeActiveSlide);

    return () => {
      document.removeEventListener('keydown', changeActiveSlide);
    };
  }, [mainSwiperInstance]);

  useEffect(() => {
    if (!thumbsSwiperInstance) return;

    thumbsSwiperInstance.slideTo(selectedPhotoIndex);
  }, [thumbsSwiperInstance, selectedPhotoIndex]);

  const goToCheckout = useCallback(async () => {
    if (hasDates) {
      try {
        setIsLoadingQuote(true);

        const {
          [ESearchFilters.DELIVERY]: delivery,
          [ESearchFilters.DELIVERY_QUERY]: deliveryQuery,
          [ESearchFilters.LOCATION_ID]: locationId,
          [ESearchFilters.DELIVERY_CENTER]: deliveryCenter,
          [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationary,
          [ESearchFilters.MAPBOX_ID]: mapbox_id,
          [ESearchFilters.FILTER_RENTER_AGE]: filterRenterAge,
        } = getParams(router);

        await dispatch(
          updateQuoteDeliveryFromURL(
            delivery,
            deliveryQuery,
            locationId,
            deliveryCenter,
            deliveryStationary,
            mapbox_id,
            true,
          ),
        );

        const quote = await dispatch(getQuote());

        router.push(
          `/checkout/${id}/new?quote=${quote?.id}&renter_age=${filterRenterAge}&quick_preview=true`,
        );
      } catch {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
          intl.formatMessage({
            defaultMessage: 'Oops, something went wrong. Please, try again later.',
            id: 'uSQB3M',
          }),
        );
      } finally {
        setIsLoadingQuote(false);
      }
    }
  }, [hasDates, router, dispatch, id, intl, showToast]);

  const firstPhotoUrl = Array.isArray(photoUrl) ? photoUrl[0] || '' : photoUrl || '';

  return (
    <Modal
      isOpen={isOpen}
      className={css.modal}
      size={EModalSize.Large}
      variant={isMobile ? EModalVariants.Bottom : undefined}
      onClose={() => {
        onClose();
      }}>
      <Modal.Actions className="md:hidden" />

      <Modal.Content className="mt-6">
        <div className="w-full md:grid md:grid-cols-[65fr_35fr] gap-8">
          <div className="hidden overflow-hidden col-1 md:block">
            <div className="w-full">
              <AspectRatio ratio="4:3" hasImageHover={false}>
                {Array.isArray(photoUrl) && photoUrl.length > 1 ? (
                  <Card.Carousel
                    ref={ref => setMainSwiperInstance(ref?.swiper)}
                    content={photoUrl.map((photo, index) => (
                      <ResponsiveImage
                        key={index}
                        src={photo || ''}
                        alt={photoAlt || title}
                        cropMode="fill"
                        role="presentation"
                        sourceSizes={['landscape572']}
                      />
                    ))}
                    className={css.mainCarousel}
                  />
                ) : (
                  <ResponsiveImage
                    src={firstPhotoUrl}
                    alt={photoAlt || title}
                    cropMode="fill"
                    role="presentation"
                    sourceSizes={['landscape572']}
                  />
                )}
              </AspectRatio>
            </div>
          </div>
          <div className="flex overflow-hidden col-2">
            <div className="flex flex-col flex-1 gap-2">
              <div className="flex flex-row">
                <Text variant={ETextStyleVariant.SmallRegular} className="flex items-center gap-2">
                  {classText}
                  <span aria-hidden="true">•</span>
                  {location}
                </Text>
                <Close onClose={onClose} className="hidden ml-auto md:block" />
              </div>
              <Heading variant={EHeadingStyleVariant.H6}>{title}</Heading>
              {ratingQtd && (
                <div className="flex flex-row">
                  <Text
                    variant={ETextStyleVariant.SmallRegular}
                    className="inline-flex items-center gap-1">
                    <GeneralStarFillIcon className="inline" />
                    <Text variant={ETextStyleVariant.SmallBold} component="span">
                      {ratingNumber}
                    </Text>
                    <Link
                      href={`${slug}/reviews`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant={ELinkVariant.Underline}>
                      (
                      {intl.formatMessage(
                        {
                          defaultMessage: '{reviews, plural, one {# review} other {# reviews}}',
                          id: 'pvspih',
                        },
                        { reviews: ratingQtd },
                      )}
                      )
                    </Link>
                  </Text>
                </div>
              )}
              {isLoadingListing && (
                <div className="flex items-center justify-center flex-1">
                  <Loader />
                </div>
              )}
              {!isLoadingListing && listingHeader && (
                <div className="flex flex-col mt-4 md:flex-col md:gap-4 md:mt-8 md:flex-nowrap">
                  {infos.map((info, index) => (
                    <div className="flex flex-row items-center mb-2 gap-4 md:mb-0" key={index}>
                      <Icon key={index} name={info.icon} height={24} width={24} />
                      <Text variant={ETextStyleVariant.MediumRegular}>{info.label}</Text>
                    </div>
                  ))}
                  {mileageRules && (
                    <div className="flex flex-row items-center mb-2 gap-4 md:mb-0">
                      <Icon name={mileageRules.icon} height={24} width={24} />
                      <Text variant={ETextStyleVariant.MediumRegular}>{mileageRules.title}</Text>
                    </div>
                  )}
                  {generatorRules && (
                    <div className="flex flex-row items-center mb-2 gap-4 md:mb-0">
                      <Icon name={generatorRules.icon} height={24} width={24} />
                      <Text variant={ETextStyleVariant.MediumRegular}>{generatorRules.title}</Text>
                    </div>
                  )}
                  {!isPetFriendly && (
                    <div className="flex flex-row items-center mb-2 gap-4 md:mb-0">
                      <Icon name={PET_NOT_ALLOWED} height={24} width={24} />
                      <Text variant={ETextStyleVariant.MediumRegular}>
                        {intl.formatMessage({ defaultMessage: 'Not pet friendly', id: 'BaTzMN' })}
                      </Text>
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-col pb-2 gap-2 md:hidden">
                {Array.isArray(photoUrl) && photoUrl.length > 1 ? (
                  photoUrl.map((photo, index) => (
                    <AspectRatio ratio="16:9" hasImageHover={false} key={index}>
                      <ResponsiveImage
                        src={photo || ''}
                        alt={photoAlt || title}
                        cropMode="fill"
                        role="presentation"
                        sourceSizes={['landscape572']}
                        className="object-cover w-full h-full rounded"
                      />
                    </AspectRatio>
                  ))
                ) : (
                  <AspectRatio ratio="16:9" hasImageHover={false}>
                    <ResponsiveImage
                      src={firstPhotoUrl}
                      alt={photoAlt || title}
                      cropMode="fill"
                      role="presentation"
                      sourceSizes={['landscape572']}
                      className="object-cover w-full h-full rounded"
                    />
                  </AspectRatio>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Content>

      <Modal.Footer className="border-t border-gray-200 md:border-none">
        <div className="w-full md:grid md:grid-cols-[65fr_35fr] gap-8">
          <div className="hidden overflow-hidden col-1 md:block">
            {Array.isArray(photoUrl) && photoUrl.length > 1 && (
              <Swiper
                onSwiper={swiper => {
                  setThumbsSwiperInstance(swiper);
                }}
                slidesPerView="auto"
                spaceBetween={8}
                freeMode={true}
                className={css.swiper}>
                {photoUrl.map((photo, index) => (
                  <SwiperSlide key={index} className={css.thumbnailSlide}>
                    <button
                      className={clsx('block w-[135px] h-[86px] rounded', {
                        'outline outline-2 outline-offset-[-2px] outline-green-500':
                          index === selectedPhotoIndex,
                      })}
                      onClick={() => handleThumbnailClick(index)}>
                      <ResponsiveImage
                        src={photo}
                        alt={photoAlt || title}
                        cropMode="fill"
                        role="presentation"
                        sourceSizes={['landscape572']}
                        className="object-cover w-full h-full rounded"
                      />
                    </button>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
          <div className="overflow-hidden col-2">
            <div className="flex flex-col gap-4">
              {price && (
                <div
                  className={clsx('flex flex-col', {
                    'overflow-hidden': hasDates,
                  })}>
                  {priceDetails}
                </div>
              )}
              <div className="flex flex-col xl:flex-row gap-2">
                <Button
                  href={`${linkUrl}&quick_preview=true`}
                  onClick={() => onClickViewListing?.()}
                  target={isAboveDesktop ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  label={intl.formatMessage({ defaultMessage: 'View listing', id: 'fJ0UJV' })}
                  fullWidth
                />
                {hasDates && (
                  <Button
                    onPress={goToCheckout}
                    label={cta}
                    variant={EButtonColorVariant.Primary}
                    loading={isLoadingQuote}
                    disabled={isLoadingQuote}
                    fullWidth
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
